import { z } from 'zod';

const num = () => z.coerce.number();
const numNull = () => z.union([z.coerce.number(), z.null()]);

export const OnlinePerformanceDailySchema = z.array(z.object({
  date: z.string(),
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  ordered_items: num(),
  delivered_items: num(),
  cancelled_items: num(),
  rtoed_items: num(),
  returned_items: num(),
  gross_gmv: num(),
  /* Gross GMV of orders not in CANCELLED / RTO / RETURNED / RETURN_WIP /
     UNFULFILLABLE status, excluding exchanges. Distinct from "Net GMV"
     (a different merch metric). */
  gross_gmv_net_rc: num(),
  mto_items: num(),
  onhand_items: num(),
  jit_items: num(),
  /* MTC split (added 2026-06-10, card 8129). Optional so older cached
     responses still parse. */
  mtc_items: num().optional().default(0),
  mto_gmv: num(),
  onhand_gmv: num(),
  jit_gmv: num(),
  mtc_gmv: num().optional().default(0),
  spend: num(),
  gross_roas: numNull(),
  net_rc_roas: numNull(),
  /* Halo ROAS components (added 2026-07-16, card 8129). Meta-attributed
     purchase value (meta_gmv) and Meta ad spend (meta_spend) per brand-day.
     Optional so older cached responses still parse; halo_roas is derived
     frontend-side as sum(meta_gmv) / sum(meta_spend). */
  meta_gmv: num().optional().default(0),
  meta_spend: num().optional().default(0),
}));
export type OnlinePerformanceDaily = z.infer<typeof OnlinePerformanceDailySchema>;

/* Merch type / Dash / sub-category mix (card 10345), companion to 8129.
   Carries no brand_manager or merch_portfolio: those are 1:1 with brand_name
   (408 brands = 408 brand x manager x portfolio combinations over 12 months),
   so the page maps them from the 8129 rows it already holds rather than
   widening this card.

   ordered_items here counts DISTINCT order_line_item_id. 8129's counts
   sales_channel_order_id, i.e. orders, so this card's figure is higher by
   roughly the items-per-order ratio (1.39 in September 2026). They are not
   interchangeable. */
export const OnlinePerformanceMerchDashMixSchema = z.array(z.object({
  date: z.string(),
  brand_name: z.string(),
  merch_type: z.string(),
  /* 'Dash' when the is_dash flag is 'true'; 'Non-Dash' for 'false' or NULL. */
  dash_segment: z.string(),
  sub_category: z.string(),
  ordered_items: num(),
  delivered_items: num(),
  returned_items: num(),
  rtoed_items: num(),
  cancelled_items: num(),
  gross_gmv: num(),
  gross_gmv_net_rc: num(),
}));
export type OnlinePerformanceMerchDashMix = z.infer<typeof OnlinePerformanceMerchDashMixSchema>;

/* Product-page traffic by brand × source (card 10118). CUBE rows: is_all_brands /
   is_all_sources mark rolled-up rows (brand_name / source null there), so unique
   users and styles are exact at every level and must never be summed. */
const flag = () => z.union([z.boolean(), z.coerce.number()]).transform(v => Boolean(v));
export const OnlineTrafficBrandSourceSchema = z.array(z.object({
  is_all_brands: flag(),
  is_all_sources: flag(),
  brand_name: z.string().nullable(),
  source: z.string().nullable(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  pdp_views: num(),
  users: num(),
  styles_viewed: num(),
  live_styles_viewed: num(),
}));
export type OnlineTrafficBrandSource = z.infer<typeof OnlineTrafficBrandSourceSchema>;

/* Top 25 styles per brand by product-page views (card 10119), with per-source
   views / users and online items ordered in the window. */
export const OnlineTopTrafficStylesSchema = z.array(z.object({
  lk_style_number: z.string(),
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  product_name: z.string().nullable(),
  category: z.string().nullable(),
  mrp: numNull(),
  image_url: z.string().nullable(),
  store_url: z.string().nullable(),
  is_live: flag(),
  pdp_views: num(),
  users: num(),
  views_app: num(), users_app: num(),
  views_meta_ads: num(), users_meta_ads: num(),
  views_meta_unclassified: num(), users_meta_unclassified: num(),
  views_google: num(), users_google: num(),
  views_instagram: num(), users_instagram: num(),
  views_crm: num(), users_crm: num(),
  views_direct: num(), users_direct: num(),
  views_other: num(), users_other: num(),
  items_ordered: num(),
}));
export type OnlineTopTrafficStyles = z.infer<typeof OnlineTopTrafficStylesSchema>;

/* Live styles per brand, current snapshot (card 10120). */
export const OnlineLiveStylesSchema = z.array(z.object({
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  live_styles: num(),
}));
export type OnlineLiveStyles = z.infer<typeof OnlineLiveStylesSchema>;

/* Online add-to-carts per brand (card 10126): cart item adds (removed items
   still count) and distinct customers who added; logged-in carts only. */
export const OnlineAddToCartsSchema = z.array(z.object({
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  atc_items: num(),
  atc_users: num(),
}));
export type OnlineAddToCarts = z.infer<typeof OnlineAddToCartsSchema>;

/* Style-grain Online Performance (card 10565), the Styles tab.

   Server-side paginated: `total_rows` is the unpaginated count repeated on
   every row via count() OVER (), so the pager reads it from row 0 without a
   second query. Inventory arrives as Array(String) of "SIZE:QTY" pairs with
   zeros already dropped. `meta_roas` is NULL when Meta spend is 0 — a style can
   carry attributed value with no spend, which makes ROAS undefined, not zero.
   Google spend is returned but deliberately excluded from meta_roas. */
export const OnlinePerformanceStyleLevelSchema = z.array(z.object({
  style: z.string(),
  brand_name: z.string().nullable(),
  product_type: z.string().nullable(),
  price_band: z.string().nullable(),
  /* Resolved per brand from mart_complete_ledger, the same way the Brands card
     does, so both tabs filter on identical values. */
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  ageing_days: numNull(),
  ordered_items: num(),
  product_views: num(),
  atc: num(),
  delivered: num(),
  returns_rtos: num(),
  return_pct: numNull(),
  net_gmv_dash: num(),
  net_gmv_non_dash: num(),
  onhand_items: num(),
  jit_items: num(),
  mto_items: num(),
  ordered_items_dash: num(),
  ordered_items_non_dash: num(),
  style_cancellation_rate: numNull(),
  view_to_atc_pct: numNull(),
  atc_to_order_pct: numNull(),
  overall_conversion_pct: numNull(),
  sizes_available: numNull(),
  sizes_total: numNull(),
  onhand_inventory: z.array(z.string()).default([]),
  jit_inventory: z.array(z.string()).default([]),
  total_inventory: num(),
  meta_spend: num(),
  google_spend: num(),
  meta_gmv: num(),
  roas: numNull(),
  meta_roas: numNull(),
  total_rows: num(),
}));
export type OnlinePerformanceStyleLevel = z.infer<typeof OnlinePerformanceStyleLevelSchema>;
